import React, { memo, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import { useLanguage } from '../../hooks';
import { useProjectConfig } from '../../hooks/useProjectConfig';
import { QuestionItemProps } from '../../types/formBuilder';
import { IntegerDictionary, QuestionInterface } from '../../types/oldWebForms';
import colors from './colors';
import { iconTypes } from './iconTypes';
import { QuestionBuilder } from './questionBuilder';
import { QuestionMenu } from './questionMenu';
import { QuestionSetting } from './questionSetting';
import { questionTypesList } from './questionTypes';
import { SectionComponent } from './sectionComponent';

const cacheIds: IntegerDictionary<string> = {};
const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 86,
  keyMapper: (index) => {
    return cacheIds[index];
  },
});

interface StyleSheet {
  [key: string]: React.CSSProperties;
}

const QuestionItem = memo(function QuestionItem(props: QuestionItemProps) {
  const {
    index,
    dataSources,
    onZoomedHandler,
    onHoveredHandler,
    onExpandedHandler,
    onAddHandler,
    onDeleteHandler,
    onDuplicateHandler,
    onTypeChange,
    onNameChange,
    onDescriptionChange,
    onStaticTextChange,
    onDateModeChange,
    onRequiredChange,
    onTextValidationChange,
    onNumberPercentageChange,
    onBarcodeTypeChange,
    onDisableGalleryChange,
    onMultiPhotoChange,
    onOptionChange,
    onMetadataCodeChange,
    onListChoicesChange,
    isDragging,
    zoomedIdInTable,
    expandedInTable,
    onZoomedInTableHandler,
    onAddInTableHandler,
    onDeleteInTableHandler,
    onDuplicateInTableHandler,
    onTypeInTableChange,
    onNameInTableChange,
    onDescriptionInTableChange,
    onStaticTextInTableChange,
    onDateModeInTableChange,
    onRequiredInTableChange,
    onBarcodeTypeInTableChange,
    onDisableGalleryInTableChange,
    onMultiPhotoInTableChange,
    onOptionInTableChange,
    onListChoicesInTableChange,
    reorderInTable,
    disableDelete,
    addSectionHandler,
    onNameInSectionChange,
    deleteSectionHandler,
    deleteSecionAndQuestionsHandler,
    duplicateSectionHandler,
    sectionCount,
    onSkipLogicChange,
    onSkipSettingChange,
    logicQuestions,
    paying,
    numberMetadataSetting,
    numberMetadataInTableSetting,
    qualityCamOnChange,
    qualityCamInTableChange,
    onFileSettingChange,
    onFileSettingInTableChange,
    onActivityPeriodTimeSettingChange,
    onActivityPeriodTimeSettingInTableChange,
    availableFileUpload,
    onRelatedListChange,
    onRelatedListInTableChange,
    onRichTextChange,
    onRichTextInTableChange,
    onNumberValidationChange,
    onNumberValidationTableChange,
    onDateValidationChange,
    onDateValidationCheckboxChange,
    onDateValidationTableChange,
    onDateValidationCheckboxTableChange,
    onDateValidationBatchChange,
    onDateValidationTableBatchChange,
    onOptionsWithCommentsChange,
    onObligatoryOptionCommentChange,
    onOptionsWithCommentsInTableChange,
    onObligatoryOptionCommentInTableChange,
    onSubformsLimitChange,
    onChecklistSettingsChange,
    onSkipLogicInTableChange,
    onSkipSettingInTableChange,
    tableLogicQuestions,
    onMeterChange,
    onAddMeterConfiguration,
    onDeleteMeterConfiguration,
    onMeterConfigurationMeterChange,
    onAddMeterConfigurationQuestion,
    onUpdateMeterConfigurationQuestion,
    onDeleteMeterConfigurationQuestion,
    onCreateAssetQuestion,
  } = props;
  const customStyles: StyleSheet = {
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
    zoomedContainerBlock: {
      position: 'relative',
      zIndex: 1,
      pointerEvents: 'none',
    },
    nonZoomedContainerBlock: {
      position: 'relative',
      zIndex: 2,
    },
  };

  const question = props.questions;
  const isZoomed = props.zoomedId;
  const isHovered = props.hoveredId;
  const isExpanded = props.expanded;
  const lang = useLanguage();
  const { blockFormBuilder } = useProjectConfig();
  if (!isDragging) cache.clear(index, 0);

  if (question.type === 'section')
    return (
      <div
        style={{
          padding: '7px 1px',
        }}
        id={question.id}
        onMouseEnter={() => onHoveredHandler(question.id)}
        onMouseLeave={() => onHoveredHandler(undefined)}
      >
        <SectionComponent
          index={index}
          isZoomed={isZoomed}
          isHovered={isHovered}
          section={question}
          onNameInSectionChange={onNameInSectionChange}
          deleteSectionHandler={deleteSectionHandler}
          deleteSecionAndQuestionsHandler={deleteSecionAndQuestionsHandler}
          duplicateSectionHandler={duplicateSectionHandler}
          sectionCount={sectionCount}
        />
      </div>
    );
  return (
    <div
      style={{
        padding: '7px 1px',
      }}
      onMouseEnter={() => onHoveredHandler(question.id)}
      onMouseLeave={() => onHoveredHandler(undefined)}
    >
      {blockFormBuilder && <div style={customStyles.overlay} />}
      {isZoomed ? (
        <Card
          id={question.id}
          elevation={3}
          style={{
            padding: '0px 2px',
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: isHovered ? colors.primary : colors.trans,
            ...(blockFormBuilder && customStyles.zoomedContainerBlock),
          }}
        >
          <CardContent style={{ padding: '16px 16px 4px' }}>
            <QuestionBuilder
              index={index}
              question={question}
              logicQuestions={logicQuestions}
              dataSources={dataSources}
              onNameChange={onNameChange}
              onDescriptionChange={onDescriptionChange}
              onStaticTextChange={onStaticTextChange}
              onDateModeChange={onDateModeChange}
              onOptionChange={onOptionChange}
              onMetadataCodeChange={onMetadataCodeChange}
              onListChoicesChange={onListChoicesChange}
              zoomedIdInTable={zoomedIdInTable}
              onZoomedInTableHandler={onZoomedInTableHandler}
              onAddInTableHandler={onAddInTableHandler}
              onDeleteInTableHandler={onDeleteInTableHandler}
              onDuplicateInTableHandler={onDuplicateInTableHandler}
              onTypeInTableChange={onTypeInTableChange}
              onNameInTableChange={onNameInTableChange}
              onDescriptionInTableChange={onDescriptionInTableChange}
              onStaticTextInTableChange={onStaticTextInTableChange}
              onDateModeInTableChange={onDateModeInTableChange}
              onOptionInTableChange={onOptionInTableChange}
              onListChoicesInTableChange={onListChoicesInTableChange}
              onRequiredInTableChange={onRequiredInTableChange}
              onTextValidationChange={onTextValidationChange}
              onNumberPercentageChange={onNumberPercentageChange}
              onBarcodeTypeInTableChange={onBarcodeTypeInTableChange}
              onDisableGalleryInTableChange={onDisableGalleryInTableChange}
              onMultiPhotoInTableChange={onMultiPhotoInTableChange}
              onExpandedHandler={onExpandedHandler}
              expandedInTable={expandedInTable}
              reorderInTable={reorderInTable}
              numberMetadataInTableSetting={numberMetadataInTableSetting}
              qualityCamOnChange={qualityCamOnChange}
              qualityCamInTableChange={qualityCamInTableChange}
              paying={paying}
              onFileSettingChange={onFileSettingChange}
              onFileSettingInTableChange={onFileSettingInTableChange}
              onActivityPeriodTimeSettingChange={onActivityPeriodTimeSettingChange}
              onActivityPeriodTimeSettingInTableChange={onActivityPeriodTimeSettingInTableChange}
              availableFileUpload={availableFileUpload}
              onRichTextChange={onRichTextChange}
              onRichTextInTableChange={onRichTextInTableChange}
              onNumberValidationChange={onNumberValidationChange}
              onRelatedListChange={onRelatedListChange}
              onRelatedListInTableChange={onRelatedListInTableChange}
              onNumberValidationTableChange={onNumberValidationTableChange}
              onDateValidationChange={onDateValidationChange}
              onDateValidationTableChange={onDateValidationTableChange}
              onDateValidationBatchChange={onDateValidationBatchChange}
              onDateValidationTableBatchChange={onDateValidationTableBatchChange}
              onDateValidationCheckboxTableChange={onDateValidationCheckboxTableChange}
              onOptionsWithCommentsInTableChange={onOptionsWithCommentsInTableChange}
              onObligatoryOptionCommentInTableChange={onObligatoryOptionCommentInTableChange}
              onChecklistSettingsChange={onChecklistSettingsChange}
              onSkipLogicInTableChange={onSkipLogicInTableChange}
              onSkipSettingInTableChange={onSkipSettingInTableChange}
              tableLogicQuestions={tableLogicQuestions}
              onMeterChange={onMeterChange}
              onAddMeterConfiguration={onAddMeterConfiguration}
              onDeleteMeterConfiguration={onDeleteMeterConfiguration}
              onMeterConfigurationMeterChange={onMeterConfigurationMeterChange}
              onAddMeterConfigurationQuestion={onAddMeterConfigurationQuestion}
              onUpdateMeterConfigurationQuestion={onUpdateMeterConfigurationQuestion}
              onDeleteMeterConfigurationQuestion={onDeleteMeterConfigurationQuestion}
              onCreateAssetQuestion={onCreateAssetQuestion}
            />
            <QuestionMenu
              index={index}
              question={question}
              onAddHandler={onAddHandler}
              onDeleteHandler={onDeleteHandler}
              onDuplicateHandler={onDuplicateHandler}
              onTypeChange={onTypeChange}
              disableDelete={disableDelete}
              addSectionHandler={addSectionHandler}
              availableFileUpload={availableFileUpload}
            />
          </CardContent>
          {questionTypesList.includes(question.type) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: 12,
                justifyContent: 'center',
              }}
              onClick={() => onExpandedHandler()}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              {lang.form_builder.advanced}
            </div>
          )}
          {isExpanded && (
            <QuestionSetting
              question={question}
              onRequiredChange={onRequiredChange}
              onTextValidationChange={onTextValidationChange}
              onNumberPercentageChange={onNumberPercentageChange}
              onBarcodeTypeChange={onBarcodeTypeChange}
              onDisableGalleryChange={onDisableGalleryChange}
              onMultiPhotoChange={onMultiPhotoChange}
              onSkipLogicChange={onSkipLogicChange}
              onSkipSettingChange={onSkipSettingChange}
              logicQuestions={logicQuestions}
              paying={paying}
              numberMetadataSetting={numberMetadataSetting}
              onRelatedListChange={onRelatedListChange}
              onOptionsWithCommentsChange={onOptionsWithCommentsChange}
              onObligatoryOptionCommentChange={onObligatoryOptionCommentChange}
              onSubformsLimitChange={onSubformsLimitChange}
              onChecklistSettingsChange={onChecklistSettingsChange}
              onMeterChange={onMeterChange}
              onAddMeterConfiguration={onAddMeterConfiguration}
              onDateValidationCheckboxChange={onDateValidationCheckboxChange}
            />
          )}
        </Card>
      ) : (
        <Card
          id={question.id}
          elevation={3}
          onClick={() => {
            onZoomedHandler(question.id);
            // onScrolledIdxHandler(index);
          }}
          style={{
            padding: '0 10px 0',
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: isHovered ? colors.primary : colors.trans,
            ...(blockFormBuilder && customStyles.nonZoomedContainerBlock),
          }}
        >
          <CardContent>
            <div
              style={{
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {
                iconTypes(15, 7, colors.primary)[
                  questionTypesList.includes(question.type) ? question.type : 'deprecated'
                ]
              }
              {
                lang.types[
                  questionTypesList.includes(question.type)
                    ? (question.type as keyof typeof lang.types)
                    : ('deprecated' as keyof typeof lang.types)
                ]
              }
            </div>
            <div style={{ textAlign: 'left', paddingLeft: 13 }}>{question.name}</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

const QuestionRow: any =
  (props: any) =>
  ({ index, style, parent }: any) => {
    const question = props.questions[index];

    if (!question) return null;

    const isZoomed = question.id === props.zoomedId;
    const isHovered = question.id === props.hoveredId;

    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        parent={parent}
        rowIndex={index}
        key={question.id}
      >
        <div style={style}>
          <Draggable
            key={question.id}
            draggableId={question.id}
            index={index}
            isDragDisabled={question.type === 'section' || props.isDragDisabled}
          >
            {(provided, snapshot) => (
              <div
                {...(index === 0 && question.type === 'section'
                  ? {}
                  : { ...provided.draggableProps })}
                {...provided.dragHandleProps}
                ref={provided.innerRef}
              >
                <QuestionItem
                  key={question.id}
                  index={index}
                  {...props}
                  questions={question}
                  zoomedId={isZoomed}
                  hoveredId={isHovered}
                  isDragging={snapshot.isDragging}
                  tableLogicQuestions={props.tableLogicQuestions}
                />
              </div>
            )}
          </Draggable>
        </div>
      </CellMeasurer>
    );
  };

const QuestionList = memo((props: any) => {
  const styles: StyleSheet = {
    mainContainer: {
      width: '100%',
    },
  };

  const { blockFormBuilder } = useProjectConfig();
  const [scrollToIndex, setScrollToIndex] = useState(-1);
  const { questions, zoomedId, hoveredId, scrolledIdx, onDragEndHandler, windowHeight } = props;

  const verticalUsedSpace = blockFormBuilder ? 160 : 72;

  useEffect(() => {
    setScrollToIndex(scrolledIdx);
  }, [scrolledIdx]);

  useEffect(() => {
    questions.forEach((q: QuestionInterface, index: number) => {
      if (cacheIds[index] && cacheIds[index] !== q.id) {
        cache.clear(index, 0);
      }

      cacheIds[index] = q.id;
    });
  }, [questions]);

  return (
    <div style={styles.mainContainer}>
      <DragDropContext onDragEnd={(event) => onDragEndHandler(event)}>
        <Droppable
          droppableId={'listDroppable'}
          mode={'virtual'}
          renderClone={(provided, snapshot, rubric) => {
            const index = rubric.source.index;
            const id = questions[index].id;
            const isZoomed = id === zoomedId;
            const isHovered = id === hoveredId;
            return (
              <div
                className='draggingQuestion'
                {...provided.draggableProps}
                ref={provided.innerRef}
                {...provided.dragHandleProps}
                key={`droppable-${index}-div-${id}`}
              >
                <QuestionItem
                  key={`droppable-${index}-question-item-${id}`}
                  index={index}
                  {...props}
                  questions={questions[index]}
                  zoomedId={isZoomed}
                  hoveredId={isHovered}
                  isDragging={snapshot.isDragging}
                  tableLogicQuestions={props.tableLogicQuestions}
                />
              </div>
            );
          }}
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              <AutoSizer disableHeight>
                {({ width }) => (
                  <List
                    height={windowHeight - verticalUsedSpace}
                    rowCount={questions.length}
                    onScroll={() => {
                      setScrollToIndex(-1);
                    }}
                    scrollToAlignment={'start'}
                    scrollToIndex={scrollToIndex}
                    rowHeight={cache.rowHeight}
                    width={width}
                    ref={(ref) => {
                      // This allow scrolling while dragging an item
                      if (ref) {
                        // eslint-disable-next-line react/no-find-dom-node
                        const listRef = ReactDOM.findDOMNode(ref);
                        if (listRef instanceof HTMLElement) {
                          provided.innerRef(listRef);
                        }
                      }
                    }}
                    rowRenderer={QuestionRow({ isDragDisabled: blockFormBuilder, ...props })}
                    deferredMeasurementCache={cache}
                    overscanRowCount={3}
                  />
                )}
              </AutoSizer>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
});

export { QuestionList };
